import { css } from "@emotion/css";
import { Badge, Callout, InlineEmptyStateOnboarding, Tab, Tabs, Tooltip, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { DebouncedTextWithLabel as DebounceText } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionProperties, ChannelResource, EnvironmentV2Resource, GetTeamsLookupBffResponseTeam, GitRefResource, ProcessTemplateParameterResource, ProcessTemplateResource, ProcessTemplateVersionSummary, ProjectSummaryResource, PropertyValueResource, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { generateSlug } from "@octopusdeploy/utilities";
import { isEmpty, isEqual } from "lodash";
import * as React from "react";
import { useEffect, useState } from "react";
import semver from "semver/preload";
import type { ProcessTemplateConsumerActionDispatcher } from "~/analytics/Analytics";
import { useAnalyticProcessTemplateConsumerActionDispatch, useAnalyticsStepEditorDispatch } from "~/analytics/Analytics";
import { CloudConnectionUsageType, getCloudConnections, isCloudTargetDiscoveryEnabled } from "~/areas/projects/components/CloudConnection/CloudConnections";
import { deleteActionAndRedirect, getDeleteProcessMenuItem, isFoundProcessTemplateDetails, isMatchingProcessTemplate, isNotFoundProcessTemplateDetails, processScopedEditPermission, } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { ErrorsForAction } from "~/areas/projects/components/Process/Common/ErrorsForAction";
import StepName from "~/areas/projects/components/Process/Common/StepName";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import type { ProcessTemplateDetails } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessTemplatesFromContext, useRefreshProcessTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessTemplatesContextProvider";
import type { FailedPermissionCheck, ProcessStepActionState } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ProcessSubPageLayout } from "~/areas/projects/components/Process/ProcessSubPageLayout";
import { ProcessTemplateActionParameterValues, shouldRenderParameter } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateActionParameterValues";
import { ProcessTemplateVersioningSection } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateVersioningSection";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import styles from "~/areas/projects/components/Process/style.module.less";
import type { StoredAction, StoredStep } from "~/areas/projects/components/Process/types";
import { isDeploymentProcessIdentifier, isDeploymentOrRunbookProcessIdentifier } from "~/areas/projects/components/Process/types";
import { getVersionMask, mapMaskToUpdateBehaviour, mapMaskToUsageConstraint, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import ActionList from "~/components/ActionList/index";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { ExpandableContainer } from "~/components/Expandable/index";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
import Logo from "~/components/Logo/index";
import Markdown from "~/components/Markdown/index";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { ExpandableFormSection, FormSection, FormSectionHeading, required, Summary, UnstructuredFormSection } from "~/components/form/index";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
interface ProcessTemplateStepDetailsProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    isNew: boolean;
    cleanAction: StoredAction | null;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    releaseCreationPackageStepId: string | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    availableTargetTags: string[];
    availableWorkerPools: WorkerPoolResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    refreshVariables: () => Promise<void>;
}
export type ConsumerProcessTemplateEditorUndefinedTab = "";
export type ConsumerProcessTemplateEditorTabs = "parameters" | "settings" | ConsumerProcessTemplateEditorUndefinedTab;
export const isConsumerProcessTemplateEditorTab = (value: string): value is ConsumerProcessTemplateEditorTabs => {
    return value === "parameters" || value === "settings" || value === "";
};
export function ProcessTemplateStepDetails({ step, action, stepOther, isNew, cleanAction, doBusyTask, gitRefResource, releaseCreationPackageStepId, processVariableSet, libraryVariableSets, environments, availableTargetTags, availableWorkerPools, isTenanted, teams, channels, projects, refreshVariables, }: ProcessTemplateStepDetailsProps) {
    const processContext = useProcessContext();
    const processErrorSelectors = useProcessErrorSelectors();
    const processTemplateContext = useProcessTemplatesFromContext();
    const refreshProcessTemplates = useRefreshProcessTemplatesFromContext();
    const isFeatureEnabled = useIsLicensedFeatureEnabled("ProcessTemplates");
    const processIdentifier = processContext.state.processIdentifier;
    if (!isDeploymentOrRunbookProcessIdentifier(processIdentifier)) {
        throw new Error("Process templates can only be used in deployment or runbook processes.");
    }
    const analyticsStepEditorDispatch = useAnalyticsStepEditorDispatch(step.Id, processIdentifier.projectId);
    const processTemplateConsumerActionDispatch = useAnalyticProcessTemplateConsumerActionDispatch(processIdentifier.projectId, isDeploymentProcessIdentifier(processIdentifier) ? "Deployment Process" : "Runbook");
    const processTemplateSlug = getProcessTemplateSlugFromAction(action);
    const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(action);
    const actionErrors = action ? processErrorSelectors.getActionErrors(action.Id, processContext.selectors) : [];
    const usageErrors = processErrorSelectors.getProcessTemplateUsageErrors(step.Id);
    const [initialActionProperties, setInitialActionProperties] = useState(action.Properties);
    const [initialCleanModel, setInitialCleanModel] = useState(processContext.state.cleanModel);
    if (!isEqual(initialCleanModel, processContext.state.cleanModel)) {
        setInitialCleanModel(processContext.state.cleanModel);
        setInitialActionProperties(action.Properties);
    }
    const processQueryStringContext = useProcessQueryStringContext();
    const [activeTab, setActiveTab] = useState<ConsumerProcessTemplateEditorTabs>(processQueryStringContext.state.queryFilter.activeTab ? processQueryStringContext.state.queryFilter.activeTab : "parameters");
    const selectedProcessTemplateDetails = processTemplateContext.find((pt) => isMatchingProcessTemplate(pt, processTemplateSlug, processTemplateVersionMask));
    const selectedProcessTemplate = isFoundProcessTemplateDetails(selectedProcessTemplateDetails) ? selectedProcessTemplateDetails.processTemplate : undefined;
    const [initialProcessTemplateVersion, setInitialProcessTemplateVersion] = useState<string | undefined | null>(selectedProcessTemplateDetails?.processTemplateVersion);
    const getFieldError = (value: string) => {
        return processErrorSelectors.getActionFieldError(action.Id, processContext.selectors, value);
    };
    const setActionMetaProperties: <K extends keyof StoredAction>(state: Pick<StoredAction, K> | StoredAction, callback?: () => void) => void = (state, callback) => {
        processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, ...state }));
        callback && callback();
    };
    const setName = (value: string) => {
        setActionMetaProperties({ Name: value });
    };
    const setSlug = (value: string) => {
        setActionMetaProperties({ Slug: value });
    };
    const setDisabled = (value: boolean) => {
        setActionMetaProperties({ IsDisabled: value });
    };
    const setVersionMaskProperty = (versionMask: string) => {
        const properties: ActionProperties = {
            "Octopus.Action.ProcessTemplate.Reference.VersionMask": versionMask,
        };
        processContext.actions.setActionProperties(action.Id, properties);
    };
    const setParameterValue = (parameterName: string, value: PropertyValueResource | undefined) => {
        if (value) {
            const properties: ActionProperties = { [parameterName]: value };
            processContext.actions.setActionProperties(action.Id, properties);
        }
        else {
            processContext.actions.removeActionProperties(action.Id, [parameterName]);
        }
    };
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(mapMaskToUsageConstraint(processTemplateVersionMask));
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(mapMaskToUpdateBehaviour(processTemplateVersionMask));
    const { result: latestProcessTemplateVersionSummary } = useQuery(async (repository) => {
        return await repository.ProcessTemplates.getVersionSummary(processTemplateSlug);
    }, [processTemplateSlug], "Get Latest Published Process Template Summary");
    const showSnackbar = useShowSnackbar();
    const latestStableVersion = latestProcessTemplateVersionSummary?.LatestStableVersion;
    const latestPreReleaseVersion = latestProcessTemplateVersionSummary?.LatestPreReleaseVersion;
    const selectedProcessTemplateVersion = selectedProcessTemplateDetails?.processTemplateVersion;
    const latestVersionToCompare = selectedUsageConstraint === UsageConstraint.PreRelease ? latestPreReleaseVersion : latestStableVersion;
    const isNewVersionAvailable = selectedProcessTemplateVersion && latestVersionToCompare ? semver.ltr(selectedProcessTemplateVersion, latestVersionToCompare) : false;
    const isAutoUpdateAvailable = isNewVersionAvailable && latestVersionToCompare && getVersionMask(latestVersionToCompare, selectedUpdateBehaviour) === processTemplateVersionMask;
    const isManualUpdateAvailable = isNewVersionAvailable && latestVersionToCompare && getVersionMask(latestVersionToCompare, selectedUpdateBehaviour) !== processTemplateVersionMask;
    useEffect(() => {
        if (isAutoUpdateAvailable) {
            (async () => await refreshProcessTemplates())();
        }
    }, [isAutoUpdateAvailable, refreshProcessTemplates]);
    const onUpdateToLatestVersion = () => {
        if (latestStableVersion) {
            if (selectedUsageConstraint === UsageConstraint.PreRelease) {
                setSelectedUsageConstraint(UsageConstraint.Stable);
                setSelectedUpdateBehaviour(UpdateBehaviour.Minor);
                const versionMask = getVersionMask(latestStableVersion, UpdateBehaviour.Minor);
                setVersionMaskProperty(versionMask);
            }
            else {
                const versionMask = getVersionMask(latestStableVersion, selectedUpdateBehaviour);
                setVersionMaskProperty(versionMask);
            }
            setInitialProcessTemplateVersion(latestStableVersion);
            showSnackbar("Process template updated to latest version.");
        }
    };
    const visibleParameters = selectedProcessTemplate?.Parameters.filter((p) => shouldRenderParameter(p, action, availableWorkerPools, isTenanted));
    const hasAnyRequiredParameterMissingValue = isAnyRequiredParameterMissingValue(visibleParameters, action.Properties);
    const visibleParametersCount = visibleParameters?.length ?? 0;
    const onTabChange = (_: React.ChangeEvent<object>, newValue: string) => {
        if (isConsumerProcessTemplateEditorTab(newValue)) {
            setActiveTab(newValue);
            processQueryStringContext.actions.changeProcessTemplateEditorActiveTab(newValue);
        }
    };
    return (<>
            <ExpandableContainer containerKey={action.Id}>
                <ActionLayout step={step} action={action} stepOther={stepOther} setDisabled={setDisabled} releaseCreationPackageStepId={releaseCreationPackageStepId} templateVersion={selectedProcessTemplateVersion}>
                    <>
                        <ActionDisabledWarning action={action}/>
                        <ProcessTemplateUsageErrorsCallout usageErrors={usageErrors}/>
                        <ErrorsForAction actionErrors={actionErrors}/>
                        <Tabs value={activeTab} onChange={onTabChange} variant="scrollable">
                            <Tab value="parameters" label={<div className={tabLabelStyles}>
                                        <div>Parameters</div>
                                        {hasAnyRequiredParameterMissingValue ? (<Tooltip content={"Required parameters are missing values"}>
                                                <ExclamationTriangleIcon size={24} color={themeTokens.color.icon.warning}/>
                                            </Tooltip>) : (<Badge size="small" variant="neutral" count={visibleParametersCount}/>)}
                                    </div>}/>
                            <Tab value="settings" label={isManualUpdateAvailable && isFeatureEnabled ? (<div className={tabLabelStyles}>
                                            <div>Settings</div>
                                            <Badge size="small" variant="neutral" label="Update available"/>
                                        </div>) : ("Settings")}/>
                        </Tabs>
                        {selectedProcessTemplateDetails ? (<>
                                {activeTab === "parameters" &&
                (selectedProcessTemplate ? (<ProcessTemplateActionParameterValues stepId={step.Id} action={action} doBusyTask={doBusyTask} onParameterValueChanged={setParameterValue} processTemplate={selectedProcessTemplate} gitRefResource={gitRefResource} libraryVariableSets={libraryVariableSets} processVariableSet={processVariableSet} environments={environments} availableWorkerPools={availableWorkerPools} availableTargetTags={availableTargetTags} analyticsStepEditorDispatch={analyticsStepEditorDispatch} isTenanted={isTenanted} teams={teams} channels={channels} projects={projects} refreshVariables={refreshVariables}/>) : (<>
                                            <div className={borderStyles}/>
                                            <InlineEmptyStateOnboarding illustration="NoEntities" entityName="parameters" title="No parameters found" description="This template may not have any parameters, or you haven’t selected a template yet."/>
                                        </>))}
                                {activeTab === "settings" && (<ProcessTemplateSettings selectedProcessTemplate={selectedProcessTemplate} isNew={isNew} action={action} getFieldError={getFieldError} setName={setName} cleanAction={cleanAction} setSlug={setSlug} initialActionProperties={initialActionProperties} initialProcessTemplateVersion={initialProcessTemplateVersion} selectedProcessTemplateDetails={selectedProcessTemplateDetails} setVersionMaskProperty={setVersionMaskProperty} latestProcessTemplateVersionSummary={latestProcessTemplateVersionSummary} selectedUpdateBehaviour={selectedUpdateBehaviour} setSelectedUpdateBehaviour={setSelectedUpdateBehaviour} selectedUsageConstraint={selectedUsageConstraint} setSelectedUsageConstraint={setSelectedUsageConstraint} processVariableSet={processVariableSet} libraryVariableSets={libraryVariableSets} processTemplateConsumerActionDispatch={processTemplateConsumerActionDispatch} onUpdateToLatestVersion={onUpdateToLatestVersion} currentVersion={selectedProcessTemplateVersion}/>)}
                            </>) : null}
                    </>
                </ActionLayout>
            </ExpandableContainer>
        </>);
}
const tabLabelStyles = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: space[8],
});
const borderStyles = css({
    marginTop: space[1],
    borderTop: `${space[1]} solid ${themeTokens.color.processStep.border.default}`,
});
interface ActionLayoutProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    setDisabled: (value: boolean) => void;
    children: JSX.Element;
    releaseCreationPackageStepId: string | undefined;
    templateVersion: string | null | undefined;
}
function ActionLayout({ step, action, setDisabled, stepOther, releaseCreationPackageStepId, children, templateVersion }: ActionLayoutProps) {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const { actions: contextActions, selectors: contextSelectors } = processContext;
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const stepActionNumber = `${contextSelectors.getStepNumber(step.Id)}`;
    const title = <StepName name={action.Name} number={stepActionNumber} stepType={stepOther.actionTypeName}/>;
    const logo = <Logo url={getActionLogoUrl(action)}/>;
    const menuItems: MenuItem[] = [];
    if (step && step.Id) {
        const processEditPermission = {
            permission: processScopedEditPermission(processContext.selectors.getProcessType()),
            project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
            wildcard: true,
        };
        const enableItem = OverflowMenuItems.item(action.IsDisabled ? "Enable" : "Disable", () => setDisabled(!action.IsDisabled), processEditPermission);
        const deleteItem = getDeleteProcessMenuItem("step", async () => deleteActionAndRedirect(step, action, true, contextActions, contextSelectors, queryStringActions), processEditPermission, releaseCreationPackageStepId, processIdentifier, step, action, processContext);
        menuItems.push(enableItem, deleteItem);
    }
    const controlActions = [<OverflowMenu menuItems={menuItems} accessibleName="More Step Actions"/>];
    const sectionControl = <ActionList actions={controlActions}/>;
    return (<ProcessSubPageLayout title={title} titleLogo={logo} titleBadge={templateVersion ? `v${templateVersion}` : undefined} sectionControl={sectionControl}>
            {children}
        </ProcessSubPageLayout>);
}
function buildNameSummary(action: StoredAction) {
    if (!action.Name) {
        return Summary.placeholder("Please enter a name for your step");
    }
    const name = (<div className={styles.nameSummaryContainer}>
            <NameSummaryWithSlug name={action.Name} slug={action.Slug || null}/>
        </div>);
    return Summary.summary(name);
}
interface ActionDisabledWarningProps {
    action: StoredAction;
}
function ActionDisabledWarning({ action }: ActionDisabledWarningProps) {
    if (!action.IsDisabled) {
        return null;
    }
    return (<UnstructuredFormSection stretchContent={true}>
            <Callout type={"warning"} title={"This step is currently disabled"}/>
        </UnstructuredFormSection>);
}
interface ProcessTemplateUsageErrorsCalloutProps {
    usageErrors: string[];
}
function ProcessTemplateUsageErrorsCallout({ usageErrors }: ProcessTemplateUsageErrorsCalloutProps) {
    if (usageErrors.length === 0) {
        return null;
    }
    return (<UnstructuredFormSection stretchContent={true}>
            <Callout type={"danger"} title={"Process template validation issues"}>
                <ul>
                    {usageErrors.map((error, index) => (<li key={index}>{error}</li>))}
                </ul>
            </Callout>
        </UnstructuredFormSection>);
}
interface ProcessTemplateSettingsProps {
    selectedProcessTemplate: ProcessTemplateResource | undefined;
    isNew: boolean;
    action: StoredAction;
    getFieldError: (value: string) => string | undefined;
    setName: (value: string) => void;
    cleanAction: StoredAction | null;
    setSlug: (value: string) => void;
    initialActionProperties: ActionProperties;
    initialProcessTemplateVersion: string | undefined | null;
    selectedProcessTemplateDetails: ProcessTemplateDetails | undefined;
    setVersionMaskProperty: (versionMask: string) => void;
    latestProcessTemplateVersionSummary: ProcessTemplateVersionSummary | null;
    selectedUpdateBehaviour: UpdateBehaviour;
    setSelectedUpdateBehaviour: React.Dispatch<React.SetStateAction<UpdateBehaviour>>;
    selectedUsageConstraint: UsageConstraint;
    setSelectedUsageConstraint: React.Dispatch<React.SetStateAction<UsageConstraint>>;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    processTemplateConsumerActionDispatch: ProcessTemplateConsumerActionDispatcher;
    onUpdateToLatestVersion: () => void;
    currentVersion: string | undefined;
}
function ProcessTemplateSettings({ selectedProcessTemplate, isNew, action, getFieldError, setName, cleanAction, setSlug, initialActionProperties, initialProcessTemplateVersion, selectedProcessTemplateDetails, setVersionMaskProperty, latestProcessTemplateVersionSummary, selectedUpdateBehaviour, setSelectedUpdateBehaviour, selectedUsageConstraint, setSelectedUsageConstraint, processVariableSet, libraryVariableSets, processTemplateConsumerActionDispatch, onUpdateToLatestVersion, currentVersion, }: ProcessTemplateSettingsProps) {
    const isProcessTemplateUnavailable = isNotFoundProcessTemplateDetails(selectedProcessTemplateDetails) && !latestProcessTemplateVersionSummary;
    return (<>
            <ExpandableFormSection isExpandedByDefault={isNew} errorKey="Name" title="Name" summary={buildNameSummary(action)} key="Name" help="A short, memorable, unique name for this process template.">
                <DebounceText value={action.Name} onChange={setName} label="Name" error={getFieldError("Name")} validate={required("Please enter a name")} autoFocus={true}/>
                {!isNew && (<SlugEditor value={action.Slug ?? generateSlug(action.Name)} name={action.Name} originalSlug={cleanAction?.Slug ?? ""} onChange={setSlug} label="Slug" error={getFieldError("Slug")} validate={required("Please enter a slug")}/>)}
            </ExpandableFormSection>
            <FormSectionHeading title="Process template details"/>
            {isProcessTemplateUnavailable ? (<Callout type="danger" title="This process template is no longer available.">
                    <div>Please delete this step and create a new one with an available template.</div>
                </Callout>) : (<FormSection title="Selected Template" includeBorder={true} help={selectedProcessTemplate?.Name}/>)}
            {selectedProcessTemplate ? (<>
                    {isEmpty(selectedProcessTemplate.Description) ? null : <FormSection title={"Description"} includeBorder={true} help={<Markdown markup={selectedProcessTemplate.Description ?? ""}/>}></FormSection>}
                    <UseCloudTargetDiscoverySection processTemplate={selectedProcessTemplate} processVariableSet={processVariableSet} libraryVariableSets={libraryVariableSets}/>
                </>) : null}
            {selectedProcessTemplate && latestProcessTemplateVersionSummary ? (<ProcessTemplateVersioningSection key={latestProcessTemplateVersionSummary.ProcessTemplateSlug} initialActionProperties={initialActionProperties} initialProcessTemplateVersion={initialProcessTemplateVersion} setVersionMaskProperty={setVersionMaskProperty} latestProcessTemplateVersionSummary={latestProcessTemplateVersionSummary} selectedUpdateBehaviour={selectedUpdateBehaviour} setSelectedUpdateBehaviour={setSelectedUpdateBehaviour} selectedUsageConstraint={selectedUsageConstraint} setSelectedUsageConstraint={setSelectedUsageConstraint} processTemplateConsumerActionDispatch={processTemplateConsumerActionDispatch} processTemplateSlug={selectedProcessTemplate.Slug} onUpdateToLatestVersion={onUpdateToLatestVersion} currentVersion={currentVersion} templateName={selectedProcessTemplate.Name}/>) : null}
        </>);
}
interface UseCloudTargetDiscoverySectionProps {
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    processTemplate: ProcessTemplateResource | undefined;
}
function UseCloudTargetDiscoverySection({ processTemplate, processVariableSet, libraryVariableSets }: UseCloudTargetDiscoverySectionProps) {
    if (!processTemplate || isFailedPermissionCheck(processVariableSet) || isFailedPermissionCheck(libraryVariableSets)) {
        return null;
    }
    const cloudTargetDiscoveryActionTypes = new Set(pluginRegistry
        .getAllActions()
        .filter((a) => a.targetDiscoveryCloudConnectionProviders)
        .map((a) => a.actionType));
    const processTemplateUsesCloudTargetDiscovery = processTemplate.Steps.some((s) => s.Actions.some((a) => cloudTargetDiscoveryActionTypes.has(a.ActionType)));
    if (!processTemplateUsesCloudTargetDiscovery) {
        return null;
    }
    const connectionType = CloudConnectionUsageType.TargetDiscovery;
    const cloudConnections = getCloudConnections(["Azure", "Aws"], connectionType, processVariableSet, libraryVariableSets);
    const isEnabled = isCloudTargetDiscoveryEnabled(cloudConnections, connectionType);
    return isEnabled ? <FormSection title="Using Cloud Target Discovery" help="Yes" includeBorder={true}/> : null;
}
export const isAnyRequiredParameterMissingValue = (parameters: ProcessTemplateParameterResource[] | undefined, actionProperties: ActionProperties): boolean => {
    return parameters?.some((p) => !p.IsOptional && p.Values.length === 0 && convertPropertyValueResourceToString(actionProperties[p.Name]) === "") ?? false;
};
